import { type MessageId } from "@cmmd-forge/contracts";
import { collapseExpandedComposerCursor } from "../../composer-logic";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type ComposerThreadDraftState,
} from "../../composerDraftStore";
import { type TerminalContextDraft } from "../../lib/terminalContext";
import { type ChatMessage } from "../../types";
import { cloneComposerImageForRetry, revokeUserMessagePreviewUrls } from "../ChatView.logic";
import { type ChatComposerHandle } from "./ChatComposerHandle";

export function restoreComposerAfterTurnStartFailure<ComposerTarget>(input: {
  readonly messageIdForSend: MessageId;
  readonly promptForSend: string;
  readonly composerImagesSnapshot: readonly ComposerImageAttachment[];
  readonly composerFilesSnapshot: readonly ComposerFileAttachment[];
  readonly composerTerminalContextsSnapshot: readonly TerminalContextDraft[];
  readonly composerDraftTarget: ComposerTarget;
  readonly readComposerDraft: (target: ComposerTarget) => ComposerThreadDraftState | null;
  readonly promptRef: { current: string };
  readonly composerImagesRef: { current: ComposerImageAttachment[] };
  readonly composerTerminalContextsRef: { current: TerminalContextDraft[] };
  readonly composerRef: { current: ChatComposerHandle | null };
  readonly setOptimisticUserMessages: (updater: (existing: ChatMessage[]) => ChatMessage[]) => void;
  readonly setComposerDraftPrompt: (target: ComposerTarget, prompt: string) => void;
  readonly addComposerDraftImages: (
    target: ComposerTarget,
    images: ComposerImageAttachment[],
  ) => void;
  readonly setComposerDraftFiles: (target: ComposerTarget, files: ComposerFileAttachment[]) => void;
  readonly setComposerDraftTerminalContexts: (
    target: ComposerTarget,
    contexts: TerminalContextDraft[],
  ) => void;
}): void {
  input.setOptimisticUserMessages((existing) => {
    const removed = existing.filter((message) => message.id === input.messageIdForSend);
    for (const message of removed) revokeUserMessagePreviewUrls(message);
    const next = existing.filter((message) => message.id !== input.messageIdForSend);
    return next.length === existing.length ? existing : next;
  });
  input.promptRef.current = input.promptForSend;
  const retryComposerImages = input.composerImagesSnapshot.map(cloneComposerImageForRetry);
  input.composerImagesRef.current = retryComposerImages;
  input.composerTerminalContextsRef.current = [...input.composerTerminalContextsSnapshot];
  input.setComposerDraftPrompt(input.composerDraftTarget, input.promptForSend);
  input.addComposerDraftImages(input.composerDraftTarget, retryComposerImages);
  const currentFiles = input.readComposerDraft(input.composerDraftTarget)?.files ?? [];
  const restoredFilesById = new Map(input.composerFilesSnapshot.map((file) => [file.id, file]));
  for (const file of currentFiles) restoredFilesById.set(file.id, file);
  input.setComposerDraftFiles(input.composerDraftTarget, [...restoredFilesById.values()]);
  input.setComposerDraftTerminalContexts(input.composerDraftTarget, [
    ...input.composerTerminalContextsSnapshot,
  ]);
  input.composerRef.current?.resetCursorState({
    cursor: collapseExpandedComposerCursor(input.promptForSend, input.promptForSend.length),
    prompt: input.promptForSend,
    detectTrigger: true,
  });
}
